import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import api, { entities } from "../services/api";
import { toast } from "../components/Toast";
import { LOW_DATA_MODE } from "../lowData";

const AppContext = createContext();

const getFromStorage = (key, defaultValue = []) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch (err) {}
  return defaultValue;
};

export function AppProvider({ children }) {
  const [counts, setCounts] = useState(() =>
    getFromStorage("counts", {
      products: 0,
      containers: 0,
      locations: 0,
      total: 0,
      trash: 0,
    }),
  );

  // Command palette state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const openCommandPalette = useCallback(() => setCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(
    () => setCommandPaletteOpen(false),
    [],
  );
  const PAGE_SIZE = 50;

  const [products, setProducts] = useState(() => getFromStorage("products"));
  const [productsHasMore, setProductsHasMore] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoadingMore, setProductsLoadingMore] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const [containers, setContainers] = useState(() =>
    getFromStorage("containers"),
  );
  const [containersHasMore, setContainersHasMore] = useState(true);
  const [containersLoading, setContainersLoading] = useState(false);
  const [containersLoadingMore, setContainersLoadingMore] = useState(false);
  const [containersError, setContainersError] = useState(null);

  const [locations, setLocations] = useState(() => getFromStorage("locations"));
  const [locationsHasMore, setLocationsHasMore] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsLoadingMore, setLocationsLoadingMore] = useState(false);
  const [locationsError, setLocationsError] = useState(null);

  const [trash, setTrash] = useState(() => getFromStorage("trash"));
  const [trashHasMore, setTrashHasMore] = useState(true);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashLoadingMore, setTrashLoadingMore] = useState(false);
  const [trashError, setTrashError] = useState(null);

  const refreshCounts = useCallback(async () => {
    if (LOW_DATA_MODE) return; // sidebar shows the last cached counts
    try {
      const [productStats, containerStats, locationStats, trashStats] =
        await Promise.all([
          entities.stats("product"),
          entities.stats("container"),
          entities.stats("location"),
          entities.trashStats(),
        ]);
      const newCounts = {
        products: productStats.data.totalCount || 0,
        containers: containerStats.data.totalCount || 0,
        locations: locationStats.data.totalCount || 0,
        total:
          (productStats.data.totalCount || 0) +
          (containerStats.data.totalCount || 0) +
          (locationStats.data.totalCount || 0),
        trash: trashStats.data.totalCount || 0,
      };
      setCounts(newCounts);
      localStorage.setItem("counts", JSON.stringify(newCounts));
    } catch (err) {
      console.error("Failed to load counts:", err);
    }
  }, []);

  // Store current sort params for products
  const [productsSortParams, setProductsSortParams] = useState({
    sort_by: "created_at",
    sort_order: "desc",
  });
  // Marketplace filter ("offerup", "ebay", "none", or null for all), kept so
  // loadMoreProducts fetches the same slice as the last refresh.
  const [productsListedOn, setProductsListedOn] = useState(null);
  // Review-content filters ({has_reviews, has_my_review} booleans, or null),
  // kept for the same reason.
  const [productsReviewFilters, setProductsReviewFilters] = useState(null);
  // Kanban drill-down ("<pipeline>.<column>" or null), same reason.
  const [productsBoardCol, setProductsBoardCol] = useState(null);
  // Active search query, kept for the same reason — loadMoreProducts and
  // implicit refreshes (delete/restore → refreshByType) must fetch the same
  // slice the user is looking at, not the unfiltered list.
  const [productsSearch, setProductsSearch] = useState(null);
  // Monotonic request id so a slow stale response can't overwrite a newer one.
  const productsRequestIdRef = useRef(0);

  const refreshProducts = useCallback(
    async (options = {}) => {
      // Support both old API (sortParams object) and new API (options with sort and search)
      const sortParams = options.sort_by
        ? options
        : options.sortParams || productsSortParams;
      const searchQuery =
        options.search !== undefined ? options.search : productsSearch;
      const listedOn =
        options.listed_on !== undefined ? options.listed_on : productsListedOn;
      const reviewFilters =
        options.review_filters !== undefined
          ? options.review_filters
          : productsReviewFilters;
      const reviewFiltered = !!(
        reviewFilters?.has_reviews ||
        reviewFilters?.has_my_review ||
        reviewFilters?.vined
      );

      if (options.sort_by || options.sortParams) {
        setProductsSortParams(sortParams);
      }
      if (options.search !== undefined) {
        setProductsSearch(options.search || null);
      }
      if (options.listed_on !== undefined) {
        setProductsListedOn(options.listed_on);
      }
      if (options.review_filters !== undefined) {
        setProductsReviewFilters(options.review_filters);
      }
      const boardCol =
        options.board !== undefined ? options.board : productsBoardCol;
      if (options.board !== undefined) {
        setProductsBoardCol(options.board);
      }

      const requestId = ++productsRequestIdRef.current;
      setProductsLoading(true);
      try {
        const queryParams = {
          entity_type: "product",
          limit: PAGE_SIZE,
          offset: 0,
          sort_by: sortParams.sort_by,
          sort_order: sortParams.sort_order,
        };

        // Add search if provided
        if (searchQuery) {
          queryParams.search = searchQuery;
        }

        if (listedOn) {
          queryParams.listed_on = listedOn;
        }

        // Values are "1" (has content) or "0" (missing it); "" means off.
        if (reviewFilters?.has_reviews) {
          queryParams.has_reviews = reviewFilters.has_reviews;
        }
        if (reviewFilters?.has_my_review) {
          queryParams.has_my_review = reviewFilters.has_my_review;
        }
        if (reviewFilters?.vined) {
          queryParams.vined = reviewFilters.vined;
        }
        if (boardCol) {
          queryParams.board = boardCol;
        }

        const { data } = await entities.list(queryParams);
        // A newer refresh started after this one — drop the stale response.
        if (requestId !== productsRequestIdRef.current) return;
        setProducts(data);
        setProductsHasMore(data.length === PAGE_SIZE);
        setProductsError(null);
        // Only cache the unfiltered view
        if (!searchQuery && !listedOn && !reviewFiltered && !boardCol) {
          localStorage.setItem("products", JSON.stringify(data));
        }
      } catch (err) {
        console.error("Failed to load products:", err);
        if (requestId === productsRequestIdRef.current) {
          setProductsError("Couldn't refresh products — showing cached data");
        }
      } finally {
        if (requestId === productsRequestIdRef.current) {
          setProductsLoading(false);
        }
      }
    },
    [
      productsSortParams,
      productsSearch,
      productsListedOn,
      productsReviewFilters,
      productsBoardCol,
    ],
  );

  const loadMoreProducts = useCallback(async () => {
    // Also bail while a refresh is in flight: this closure's `products` and
    // offset belong to the list being replaced, and the request id below only
    // catches refreshes that start *after* this point.
    if (productsLoading || productsLoadingMore || !productsHasMore) return;
    // A refresh started after this point invalidates the append — its
    // response reflects newer filters/sort than the list captured here.
    const requestId = productsRequestIdRef.current;
    setProductsLoadingMore(true);
    try {
      const { data } = await entities.list({
        entity_type: "product",
        limit: PAGE_SIZE,
        offset: products.length,
        sort_by: productsSortParams.sort_by,
        sort_order: productsSortParams.sort_order,
        ...(productsSearch ? { search: productsSearch } : {}),
        ...(productsListedOn ? { listed_on: productsListedOn } : {}),
        ...(productsReviewFilters?.has_reviews
          ? { has_reviews: productsReviewFilters.has_reviews }
          : {}),
        ...(productsReviewFilters?.has_my_review
          ? { has_my_review: productsReviewFilters.has_my_review }
          : {}),
        ...(productsReviewFilters?.vined
          ? { vined: productsReviewFilters.vined }
          : {}),
        ...(productsBoardCol ? { board: productsBoardCol } : {}),
      });
      if (requestId !== productsRequestIdRef.current) return;
      const newProducts = [...products, ...data];
      setProducts(newProducts);
      setProductsHasMore(data.length === PAGE_SIZE);
      if (
        !productsSearch &&
        !productsListedOn &&
        !productsReviewFilters?.has_reviews &&
        !productsReviewFilters?.has_my_review &&
        !productsReviewFilters?.vined &&
        !productsBoardCol
      ) {
        localStorage.setItem("products", JSON.stringify(newProducts));
      }
    } catch (err) {
      console.error("Failed to load more products:", err);
      toast.error("Failed to load more products");
    } finally {
      setProductsLoadingMore(false);
    }
  }, [
    products,
    productsLoading,
    productsLoadingMore,
    productsHasMore,
    productsSortParams,
    productsSearch,
    productsListedOn,
    productsReviewFilters,
    productsBoardCol,
  ]);

  // Store current sort params for containers
  const [containersSortParams, setContainersSortParams] = useState({
    sort_by: "created_at",
    sort_order: "desc",
  });
  // Monotonic request id so a slow stale response can't overwrite a newer one.
  const containersRequestIdRef = useRef(0);

  const refreshContainers = useCallback(
    async (sortParams) => {
      const params = sortParams || containersSortParams;
      if (sortParams) {
        setContainersSortParams(sortParams);
      }

      const requestId = ++containersRequestIdRef.current;
      setContainersLoading(true);
      try {
        const { data } = await entities.list({
          entity_type: "container",
          limit: PAGE_SIZE,
          offset: 0,
          sort_by: params.sort_by,
          sort_order: params.sort_order,
        });
        if (requestId !== containersRequestIdRef.current) return;
        setContainers(data);
        setContainersHasMore(data.length === PAGE_SIZE);
        setContainersError(null);
        localStorage.setItem("containers", JSON.stringify(data));
      } catch (err) {
        console.error("Failed to load containers:", err);
        if (requestId === containersRequestIdRef.current) {
          setContainersError(
            "Couldn't refresh containers — showing cached data",
          );
        }
      } finally {
        if (requestId === containersRequestIdRef.current) {
          setContainersLoading(false);
        }
      }
    },
    [containersSortParams],
  );

  const loadMoreContainers = useCallback(async () => {
    if (containersLoading || containersLoadingMore || !containersHasMore)
      return;
    const requestId = containersRequestIdRef.current;
    setContainersLoadingMore(true);
    try {
      const { data } = await entities.list({
        entity_type: "container",
        limit: PAGE_SIZE,
        offset: containers.length,
        sort_by: containersSortParams.sort_by,
        sort_order: containersSortParams.sort_order,
      });
      if (requestId !== containersRequestIdRef.current) return;
      const newContainers = [...containers, ...data];
      setContainers(newContainers);
      setContainersHasMore(data.length === PAGE_SIZE);
      localStorage.setItem("containers", JSON.stringify(newContainers));
    } catch (err) {
      console.error("Failed to load more containers:", err);
      toast.error("Failed to load more containers");
    } finally {
      setContainersLoadingMore(false);
    }
  }, [
    containers,
    containersLoading,
    containersLoadingMore,
    containersHasMore,
    containersSortParams,
  ]);

  // Store current sort params for locations
  const [locationsSortParams, setLocationsSortParams] = useState({
    sort_by: "created_at",
    sort_order: "desc",
  });
  // Monotonic request id so a slow stale response can't overwrite a newer one.
  const locationsRequestIdRef = useRef(0);

  const refreshLocations = useCallback(
    async (sortParams) => {
      const params = sortParams || locationsSortParams;
      if (sortParams) {
        setLocationsSortParams(sortParams);
      }

      const requestId = ++locationsRequestIdRef.current;
      setLocationsLoading(true);
      try {
        const { data } = await entities.list({
          entity_type: "location",
          limit: PAGE_SIZE,
          offset: 0,
          sort_by: params.sort_by,
          sort_order: params.sort_order,
        });
        if (requestId !== locationsRequestIdRef.current) return;
        setLocations(data);
        setLocationsHasMore(data.length === PAGE_SIZE);
        setLocationsError(null);
        localStorage.setItem("locations", JSON.stringify(data));
      } catch (err) {
        console.error("Failed to load locations:", err);
        if (requestId === locationsRequestIdRef.current) {
          setLocationsError("Couldn't refresh locations — showing cached data");
        }
      } finally {
        if (requestId === locationsRequestIdRef.current) {
          setLocationsLoading(false);
        }
      }
    },
    [locationsSortParams],
  );

  const loadMoreLocations = useCallback(async () => {
    if (locationsLoadingMore || !locationsHasMore) return;
    const requestId = locationsRequestIdRef.current;
    setLocationsLoadingMore(true);
    try {
      const { data } = await entities.list({
        entity_type: "location",
        limit: PAGE_SIZE,
        offset: locations.length,
        sort_by: locationsSortParams.sort_by,
        sort_order: locationsSortParams.sort_order,
      });
      if (requestId !== locationsRequestIdRef.current) return;
      const newLocations = [...locations, ...data];
      setLocations(newLocations);
      setLocationsHasMore(data.length === PAGE_SIZE);
      localStorage.setItem("locations", JSON.stringify(newLocations));
    } catch (err) {
      console.error("Failed to load more locations:", err);
      toast.error("Failed to load more locations");
    } finally {
      setLocationsLoadingMore(false);
    }
  }, [locations, locationsLoadingMore, locationsHasMore, locationsSortParams]);

  // Monotonic request id so a slow stale response can't overwrite a newer one.
  const trashRequestIdRef = useRef(0);

  const refreshTrash = useCallback(async () => {
    const requestId = ++trashRequestIdRef.current;
    setTrashLoading(true);
    try {
      const { data } = await entities.listTrash({
        limit: PAGE_SIZE,
        offset: 0,
      });
      if (requestId !== trashRequestIdRef.current) return;
      setTrash(data);
      setTrashHasMore(data.length === PAGE_SIZE);
      setTrashError(null);
      localStorage.setItem("trash", JSON.stringify(data));
    } catch (err) {
      console.error("Failed to load trash:", err);
      if (requestId === trashRequestIdRef.current) {
        setTrashError("Couldn't refresh trash — showing cached data");
      }
    } finally {
      if (requestId === trashRequestIdRef.current) {
        setTrashLoading(false);
      }
    }
  }, []);

  const loadMoreTrash = useCallback(async () => {
    if (trashLoadingMore || !trashHasMore) return;
    const requestId = trashRequestIdRef.current;
    setTrashLoadingMore(true);
    try {
      const { data } = await entities.listTrash({
        limit: PAGE_SIZE,
        offset: trash.length,
      });
      if (requestId !== trashRequestIdRef.current) return;
      const newTrash = [...trash, ...data];
      setTrash(newTrash);
      setTrashHasMore(data.length === PAGE_SIZE);
      localStorage.setItem("trash", JSON.stringify(newTrash));
    } catch (err) {
      console.error("Failed to load more trash:", err);
      toast.error("Failed to load more trash items");
    } finally {
      setTrashLoadingMore(false);
    }
  }, [trash, trashLoadingMore, trashHasMore]);

  // Helper to refresh data after entity type changes
  const refreshByType = useCallback(
    (entityType) => {
      switch (entityType) {
        case "product":
          refreshProducts();
          break;
        case "container":
          refreshContainers();
          break;
        case "location":
          refreshLocations();
          break;
        default:
          break;
      }
    },
    [refreshProducts, refreshContainers, refreshLocations],
  );

  // Wrapper for delete - refreshes trash and counts automatically
  const deleteEntity = useCallback(
    async (id, entityType) => {
      await entities.delete(id);
      refreshByType(entityType);
      refreshTrash();
      refreshCounts();
    },
    [refreshByType, refreshTrash, refreshCounts],
  );

  // Wrapper for bulk delete — one server call, with a chunked per-id fallback
  // for servers that don't have the bulk endpoint yet.
  const deleteEntities = useCallback(
    async (ids, entityType) => {
      try {
        // The server caps bulk-delete at 100 ids per request, so send slices.
        for (let i = 0; i < ids.length; i += 100) {
          const slice = ids.slice(i, i + 100);
          try {
            await api.post("/entities/bulk-delete", { ids: slice });
          } catch (err) {
            // A 404 on the first slice means the route doesn't exist (old
            // server); later slices already proved it does, so rethrow.
            if (err.response?.status !== 404 || i > 0) throw err;
            // Old server — fall back to per-id deletes, chunked so a large
            // selection doesn't fire thousands of simultaneous requests.
            const results = [];
            for (let j = 0; j < ids.length; j += 10) {
              const chunk = ids.slice(j, j + 10);
              results.push(
                ...(await Promise.allSettled(
                  chunk.map((id) => entities.delete(id)),
                )),
              );
            }
            const failed = results.filter(
              (r) => r.status === "rejected",
            ).length;
            if (failed > 0) {
              throw new Error(
                `Failed to delete ${failed} of ${ids.length} items`,
              );
            }
            break;
          }
        }
      } finally {
        // Refresh even on partial failure so the lists reflect what did get
        // deleted instead of leaving deleted rows on screen.
        refreshByType(entityType);
        refreshTrash();
        refreshCounts();
      }
    },
    [refreshByType, refreshTrash, refreshCounts],
  );

  // Wrapper for restore - refreshes source list and counts automatically
  const restoreEntity = useCallback(
    async (id, entityType, clearQrCode = false) => {
      await entities.restore(id, clearQrCode);
      refreshByType(entityType);
      refreshTrash();
      refreshCounts();
    },
    [refreshByType, refreshTrash, refreshCounts],
  );

  // Wrapper for permanent delete
  const permanentDeleteEntity = useCallback(
    async (id) => {
      await entities.permanentDelete(id);
      refreshTrash();
      refreshCounts();
    },
    [refreshTrash, refreshCounts],
  );

  // Wrapper for empty trash
  const emptyTrashAction = useCallback(async () => {
    await entities.emptyTrash();
    refreshTrash();
    refreshCounts();
  }, [refreshTrash, refreshCounts]);

  return (
    <AppContext.Provider
      value={{
        counts,
        refreshCounts,
        products,
        productsLoading,
        productsLoadingMore,
        productsHasMore,
        productsError,
        refreshProducts,
        loadMoreProducts,
        containers,
        containersLoading,
        containersLoadingMore,
        containersHasMore,
        containersError,
        refreshContainers,
        loadMoreContainers,
        locations,
        locationsLoading,
        locationsLoadingMore,
        locationsHasMore,
        locationsError,
        refreshLocations,
        loadMoreLocations,
        trash,
        trashLoading,
        trashLoadingMore,
        trashHasMore,
        trashError,
        refreshTrash,
        loadMoreTrash,
        // Action wrappers that auto-refresh
        deleteEntity,
        deleteEntities,
        restoreEntity,
        permanentDeleteEntity,
        emptyTrashAction,
        commandPaletteOpen,
        openCommandPalette,
        closeCommandPalette,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
